import AbstractRowTemplate from "./AbstractRowTemplate.js";
import accountOperationFactory from "../../../keyFigure/accountOperations/accountOperationFactory.js";
import specificationFactory from "../../../specification/specificationFactory.js";

/**
 * Abstract template for row that contain a calculated value (i.e. not a
 * text nor a spacer row template).
 *
 * @abstract
 * @param {specification} [spec.defaultDimensionSpecification] - Default
 * dimension specification used for row instances.
 * @param {accountOperation} [spec.accountOperation] - The account operation
 * of the row if any.
 * @param {boolean} [spec.visibleWhenNoData] - When true, the receiver's
 * visibility is not impacted if there is no corresponding data.
 */
export default class AbstractValueRowTemplate extends AbstractRowTemplate {
	constructor({
		defaultDimensionSpecification,
		accountOperation,
		visibleWhenNoData = false,
	} = {}) {
		super(...arguments);
		this._defaultDimensionSpecification = defaultDimensionSpecification;
		this._accountOperation = accountOperation;

		this._hasDimensionSpecification = !!defaultDimensionSpecification;
		this._visibleWhenNoData = visibleWhenNoData;
	}

	getDefaultDimensionSpecification() {
		const parent = this.getParent();

		if (parent.isRow()) {
			const parentDimensionSpecification =
				parent.getDefaultDimensionSpecification();

			if (parentDimensionSpecification) {
				return parentDimensionSpecification;
			}
		}

		return this._defaultDimensionSpecification;
	}

	isVisibleWhenNoData() {
		return this._visibleWhenNoData;
	}

	setVisibleWhenNoData(visibleWhenNoData) {
		this._visibleWhenNoData = visibleWhenNoData;
	}

	setDefaultDimensionSpecification(dimensionSpecification) {
		this._defaultDimensionSpecification = dimensionSpecification;
	}

	hasOwnDimensionSpecification() {
		return this._hasDimensionSpecification;
	}

	setHasDimensionSpecification(boolean) {
		this._hasDimensionSpecification = boolean;
		if (!this._hasDimensionSpecification) {
			this._defaultDimensionSpecification = null;
		}
	}

	/**
	 * Returns the account operation of the row. Parent's account operation,
	 * if any, takes precedence.
	 */
	getAccountOperation() {
		const parentOperation =
			this.getParent().isRow() && this.getParent().getAccountOperation();
		return parentOperation || this._accountOperation;
	}

	setAccountOperation(accountOperation) {
		this._accountOperation = accountOperation;
	}

	accept(visitor) {
		return visitor.visitAbstractValueRowTemplate(this);
	}

	_interpretationSpec(interpreter) {
		const spec = super._interpretationSpec(...arguments);
		const context = interpreter.getContext();

		spec.dimensionSpecification =
			context.getDimensionSpecificationForTemplate(this) ||
			this._defaultDimensionSpecification;

		spec.dimensionTree = context.getDimensionTree();
		spec.accountOperation = this._accountOperation;
		spec.visibleWhenNoData = this._visibleWhenNoData;

		return spec;
	}

	_serializedProperties() {
		const properties = super._serializedProperties();

		properties.defaultDimensionSpecification =
			this._defaultDimensionSpecification;

		properties.accountOperation = this._accountOperation;
		properties.visibleWhenNoData = this._visibleWhenNoData;

		return properties;
	}

	static transformMaterializationData(data, context) {
		super.transformMaterializationData(...arguments);

		data.accountOperation &&= accountOperationFactory.materialize(
			data.accountOperation,
			context,
		);

		data.defaultDimensionSpecification &&= specificationFactory.materialize(
			data.defaultDimensionSpecification,
		);
	}
}
